import {
  skyUpgradeOffer,
  skyEquipment,
  SKY_UPGRADE_PATHS,
} from '../../../packages/domain/src/77-sky-upgrades.mjs';

/** Earned equipment presentation only. Domain offers own prices and mastery;
 * stable button nodes retain keyboard focus when purchases update their content. */
export function createSkyUpgradeDock(e) {
  const element = e('div', 'sky-upgrades');
  element.setAttribute('aria-label', 'Equipment workshop');
  const heading = e('div', 'sky-workshop-heading');
  const label = e('span', 'sky-workshop-label', 'EQUIPMENT / UPLINK');
  const credits = e('strong', 'sky-workshop-credits');
  heading.append(label, credits);
  element.append(heading);
  let selected = null;
  const buttons = ['weapon', 'dual', 'drone', 'nova', 'booster', 'shield', 'repair'].map((key) => {
    const button = e('button', 'sky-upgrade');
    button.type = 'button';
    button.dataset.upgrade = key;
    const icon = e('span', `sky-equipment-icon sky-equipment-${key}`);
    icon.setAttribute('aria-hidden', 'true');
    const name = e('strong', 'sky-upgrade-name');
    const tier = e('span', 'sky-upgrade-tier');
    const rails = e('span', 'sky-tier-rails');
    rails.setAttribute('aria-hidden', 'true');
    const segments = Array.from({ length: SKY_UPGRADE_PATHS[key]?.maxLevel ?? 1 }, () => {
      const segment = e('i', 'sky-tier-segment');
      rails.append(segment);
      return segment;
    });
    const benefit = e('span', 'sky-upgrade-benefit');
    const requirement = e('span', 'sky-upgrade-requirement');
    const price = e('strong', 'sky-upgrade-price');
    button.append(icon, name, tier, rails, benefit, requirement, price);
    element.append(button);
    return { key, button, name, tier, segments, benefit, requirement, price };
  });
  function render(state) {
    credits.textContent = `${state.salvage} SALVAGE CREDITS`;
    for (const card of buttons) {
      const { key, button } = card;
      const offer = skyUpgradeOffer(state, key);
      const path = SKY_UPGRADE_PATHS[key];
      const level = state.upgrades[key] ?? 0;
      const next = Math.min(path?.maxLevel ?? 0, level + 1);
      const equipment = skyEquipment({ ...state.upgrades, [key]: next });
      card.name.textContent = key === 'repair' ? 'Shield repair' : path.name;
      card.tier.textContent =
        key === 'repair'
          ? `${state.lives}/${equipment.maxLives} SHIELDS`
          : `TIER ${level} / ${path.maxLevel}`;
      for (const [index, segment] of card.segments.entries())
        segment.dataset.filled = String(
          key === 'repair' ? state.lives >= equipment.maxLives : index < level,
        );
      const benefits = {
        weapon: `${Math.round(equipment.aimStrength * 100)}% aim correction · ${equipment.shotSpeed}m/s bolts · ${equipment.shotDamage} damage · ${Math.round(1000 * equipment.shotInterval)}ms fire${equipment.shotPierce ? ` · pierces ${equipment.shotPierce} in overdrive` : ''}`,
        nova: `${equipment.novaDamage} guardian damage · clears threats`,
        booster: `${equipment.lateralSpeed}m/s steering · ${equipment.overdriveSeconds}s overdrive`,
        shield: `${equipment.maxLives} shields · ${equipment.shieldGrace}s protection · restore one`,
        dual: `Two bolts per volley · ${Math.round(equipment.dualDamageScale * 100)}% damage each · ${Math.round(equipment.dualDamageScale * 200)}% total power`,
        drone: `${equipment.droneCount} escort${equipment.droneCount === 1 ? '' : 's'} · autonomous ballistic fire · no precision rewards`,
        repair: 'Restore one shield',
      };
      card.benefit.textContent = benefits[key];
      card.requirement.textContent = offer.requiredCriticalHits
        ? `${Math.min(state.criticalHits, offer.requiredCriticalHits)}/${offer.requiredCriticalHits} precision hits`
        : offer.reason === 'insufficient-salvage'
          ? 'Earn more salvage'
          : offer.reason === 'max-level'
            ? 'Fully equipped'
            : offer.reason === 'shields-full'
              ? 'Shields at capacity'
              : 'Ready to install';
      card.price.textContent = offer.cost
        ? `${offer.cost} CREDITS · ${offer.ok ? 'INSTALL' : 'LOCKED'}`
        : key === 'repair'
          ? 'FULL'
          : 'MAX TIER';
      button.setAttribute(
        'aria-label',
        `${card.name.textContent}. ${card.tier.textContent}. ${card.benefit.textContent}. ${card.requirement.textContent}. ${card.price.textContent}`,
      );
      button.disabled = !offer.ok;
      button.dataset.locked = String(offer.reason === 'mastery-required');
      button.dataset.selected = String(selected === key);
    }
  }
  return {
    element,
    buttons,
    render,
    select(key) {
      selected = key;
    },
  };
}
